// src/components/common/AppPageHeader.tsx

import React, { useMemo, useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useMatches,
  useNavigate,
} from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Stack,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ChevronLeft, MoreVert } from '@mui/icons-material';

import { userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';

import type {
  HeaderAction,
  PageHeaderBack,
  PageHeaderSlots,
  PageHeaderLeading,
} from './pageHeader.types';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type BreadcrumbItem = {
  label: string;
  to?: string;
};

export type AppPageHeaderMeta = {
  /** Whether to show breadcrumbs (default: true) */
  breadcrumb?: boolean;

  /** Optional breadcrumb label (prefer `handle.crumb` at route level) */
  crumb?: string;

  /** Main title */
  title?: string;

  /** Supporting description under the title */
  description?: string;

  /** Back button behavior: true -> navigate(-1), string -> navigate(path), function -> custom handler */
  back?: PageHeaderBack;

  /**
   * Action area:
   * - 'slot' uses injected slot actions (HeaderAction[])
   * - ReactNode supports legacy inline rendering
   */
  actions?: 'slot' | React.ReactNode;

  /**
   * Maximum content width for the page header.
   * - If not provided, the header spans full width.
   * - Use this to align header with constrained page layouts.
   */
  maxWidth?: number;

  /**
   * Leading area:
   * - 'slot' uses injected slot leading
   * - PageHeaderLeading supports inline leading meta
   */
  leading?: 'slot' | PageHeaderLeading;
};

type RouteHandle = {
  pageHeader?: AppPageHeaderMeta;
  crumb?: string;
};

type AppPageHeaderProps = {
  slots?: PageHeaderSlots;
};

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Normalize action list:
 * - Ensure at most one primary action
 * - Primary action is always placed at the far right
 */
function normalizeActions(actions: HeaderAction[]) {
  const list = [...actions];

  const primaryIndex = list.findIndex((a) => a.primary);
  const primary = primaryIndex >= 0 ? list.splice(primaryIndex, 1)[0] : list.pop();

  return {
    ordered: primary ? [...list, primary] : list,
    primary,
  };
}

/* ------------------------------------------------------------------ */
/* ActionBar                                                          */
/* ------------------------------------------------------------------ */

function ActionBar({ actions }: { actions: HeaderAction[] }) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { ordered, primary } = useMemo(() => normalizeActions(actions), [actions]);

  if (!ordered.length) return null;

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  // Simple case: render all actions directly
  if (ordered.length <= 3) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {ordered.map((a) => (
          <Button
            key={a.key}
            component={a.to ? RouterLink : a.href ? 'a' : 'button'}
            to={a.to as any}
            href={a.href}
            onClick={a.onClick}
            startIcon={a.startIcon}
            variant={a.variant ?? (a.primary ? 'contained' : 'outlined')}
            color={a.color ?? 'primary'}
            disabled={a.disabled}
          >
            {a.label}
          </Button>
        ))}
      </Stack>
    );
  }

  // Complex case: fold extra secondary actions into menu
  const secondary = primary ? ordered.slice(0, -1) : ordered.slice(0, -1);
  const visible = secondary.slice(0, 2);
  const folded = secondary.slice(2);
  const primaryAction = primary ?? ordered[ordered.length - 1];

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        {visible.map((a) => (
          <Button
            key={a.key}
            component={a.to ? RouterLink : a.href ? 'a' : 'button'}
            to={a.to as any}
            href={a.href}
            onClick={a.onClick}
            startIcon={a.startIcon}
            variant={a.variant ?? 'outlined'}
            color={a.color ?? 'primary'}
            disabled={a.disabled}
          >
            {a.label}
          </Button>
        ))}

        <IconButton
          size="small"
          onClick={openMenu}
          aria-label="More actions"
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
        >
          <MoreVert fontSize="small" />
        </IconButton>

        <Button
          component={primaryAction.to ? RouterLink : primaryAction.href ? 'a' : 'button'}
          to={primaryAction.to as any}
          href={primaryAction.href}
          onClick={primaryAction.onClick}
          startIcon={primaryAction.startIcon}
          variant={primaryAction.variant ?? 'contained'}
          color={primaryAction.color ?? 'primary'}
          disabled={primaryAction.disabled}
        >
          {primaryAction.label}
        </Button>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {folded.map((a) => (
          <MenuItem
            key={a.key}
            disabled={a.disabled}
            onClick={() => {
              closeMenu();
              a.onClick?.();
            }}
            component={a.to ? RouterLink : a.href ? 'a' : 'li'}
            to={a.to as any}
            href={a.href}
          >
            {a.startIcon && <ListItemIcon>{a.startIcon}</ListItemIcon>}
            <ListItemText>{a.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* AppPageHeader                                                      */
/* ------------------------------------------------------------------ */

const AppPageHeader = ({ slots }: AppPageHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMatches();
  const user = useRecoilValue(userState);

  const homeTo = user?.group === UserGroup.PARTNER ? '/orders' : '/home';

  /**
   * Resolve page header meta from the deepest matched route.
   * (Leaf route takes precedence.)
   */
  const meta = useMemo<AppPageHeaderMeta | undefined>(() => {
    return [...matches]
      .map((m) => (m.handle as RouteHandle | undefined)?.pageHeader)
      .filter(Boolean)
      .at(-1);
  }, [matches]);

  const maxWidth = meta?.maxWidth;
  const showBreadcrumb = meta?.breadcrumb !== false;

  /**
   * Build breadcrumb items from route handles.
   * - Uses `handle.crumb` (preferred), falls back to `handle.pageHeader.crumb`
   * - Removes consecutive duplicate labels
   */
  const crumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Home', to: homeTo }];

    matches.forEach((m) => {
      const h = m.handle as RouteHandle | undefined;
      const label = h?.crumb ?? h?.pageHeader?.crumb;
      if (!label || label === items.at(-1)?.label) return;

      items.push({
        label,
        to: (m as any)?.pathnameBase,
      });
    });

    return items;
  }, [matches, homeTo]);

  const back = slots?.back ?? meta?.back;
  const from = useMemo(() => {
    const stateFrom = (location.state as any)?.from;
    return typeof stateFrom === 'string' && stateFrom.trim() ? stateFrom : undefined;
  }, [location.state]);

  const handleBack = () => {
    if (!back) return;
    if (typeof back === 'function') {
      back();
      return;
    }
    if (typeof back === 'string') {
      navigate(back);
      return;
    }
    if (from) {
      navigate(from);
      return;
    }
    navigate(-1);
  };

  /**
   * Resolve leading:
   * - If meta requests slot, use injected slot leading only
   * - Otherwise use meta leading if provided, else fallback to slot leading
   */
  const leading =
    meta?.leading === 'slot' ? slots?.leading : meta?.leading ?? slots?.leading;

  /**
   * Resolve actions:
   * - If meta requests slot, use injected slot actions (HeaderAction[])
   * - If meta provides a ReactNode, render it as legacy inline actions
   * - Otherwise fallback to injected slot actions
   */
  const actionsResolved: HeaderAction[] | React.ReactNode | undefined = (() => {
    if (meta?.actions === 'slot') return slots?.actions;
    if (meta?.actions && meta.actions !== 'slot') return meta.actions;
    return slots?.actions;
  })();

  const hasActions = Array.isArray(actionsResolved)
    ? actionsResolved.length > 0
    : Boolean(actionsResolved);

  const hasTitleBlock =
    meta?.title ||
    meta?.description ||
    back ||
    leading?.title ||
    leading?.description ||
    hasActions;

  if (!showBreadcrumb && !hasTitleBlock) return null;

  return (
    <Box sx={{ mb: 2, width: '100%' }}>
      {/* Navigation */}
      {(back || showBreadcrumb) && (
        <Box
          sx={{
            maxWidth: maxWidth ? maxWidth : '100%',
            mb: hasTitleBlock ? 0.75 : 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
          }}
        >
          {back && (
            <Button
              size="small"
              startIcon={<ChevronLeft fontSize="small" />}
              onClick={handleBack}
              sx={{
                px: 0,
                minHeight: 24,
                fontWeight: 700,
                color: 'primary.main',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                },
              }}
            >
              Back
            </Button>
          )}

          {showBreadcrumb && (
            <Breadcrumbs
              aria-label="breadcrumb"
              sx={{
                color: 'text.secondary',
                '& .MuiBreadcrumbs-separator': {
                  mx: 0.75,
                  color: 'text.disabled',
                },
              }}
            >
              {crumbs.map((c, idx) =>
                idx === crumbs.length - 1 || !c.to ? (
                  <Typography key={idx} variant="caption" color="text.secondary">
                    {c.label}
                  </Typography>
                ) : (
                  <Link
                    key={idx}
                    component={RouterLink}
                    to={c.to}
                    underline="hover"
                    variant="caption"
                    color="text.secondary"
                  >
                    {c.label}
                  </Link>
                ),
              )}
            </Breadcrumbs>
          )}
        </Box>
      )}

      {/* Title / Leading / Actions */}
      {hasTitleBlock && (
        <Box sx={{ maxWidth: maxWidth ? maxWidth : '100%', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'flex-start' },
              justifyContent: 'space-between',
              gap: { xs: 1.5, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Stack
              alignItems="flex-start"
              sx={{ minWidth: 0, flex: '1 1 auto' }}
            >
              <Box sx={{ minWidth: 0 }}>
                {meta?.title && (
                  <Typography
                    variant="h4"
                    fontWeight={900}
                    sx={{
                      lineHeight: 1.15,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {meta.title}
                  </Typography>
                )}

                {meta?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {meta.description}
                  </Typography>
                )}

                {(leading?.title || leading?.description) && (
                  <Box sx={{ mt: meta?.title || meta?.description ? 0.75 : 0 }}>
                    {leading?.title && (
                      <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        sx={{
                          lineHeight: 1.25,
                          color: 'text.primary',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {leading.title}
                      </Typography>
                    )}
                    {leading?.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {leading.description}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Stack>

            {/* Actions (always right-aligned) */}
            {Array.isArray(actionsResolved) ? (
              actionsResolved.length > 0 ? (
                <Box
                  sx={{
                    ml: { xs: 0, sm: 'auto' },
                    flex: '0 0 auto',
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  }}
                >
                  <ActionBar actions={actionsResolved} />
                </Box>
              ) : null
            ) : actionsResolved ? (
              <Box
                sx={{
                  ml: { xs: 0, sm: 'auto' },
                  flex: '0 0 auto',
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                {actionsResolved}
              </Box>
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AppPageHeader;
